/**
 * Responsibility: decide where a radio telescope array stands, and lay out its dishes.
 *
 * ## The first installation in the world, and the pattern matters more than the feature
 *
 * Everything placed so far is either a region (the city), a tiling of a region (farmland's fields), or a scatter
 * across one (trees, rock columns, arches). An array is none of those: it is a single *compound* at one spot, with a
 * deliberate internal arrangement, sitting on whatever biome happens to suit it. A railyard, an airfield, an
 * industrial plant and a set of ruins are all the same problem, so this is worth getting right on the cheapest case.
 *
 * Two things fall out of "compound" that a scatter never has to deal with:
 *
 * 1. **A site is bigger than the gap between tiles.** So a tile cannot ask "which sites are mine" — it has to ask
 *    "which dishes land in me", looking at every site whose reach overlaps. A dish near a tile edge belongs to
 *    exactly one tile, but the *site* it belongs to is shared.
 * 2. **A site is all-or-nothing.** Half an array is not a rarer array, it is a broken one. So the ground is checked
 *    across every dish position before any of them is emitted, and a site that fails is dropped whole.
 *
 * ## One authoritative site rule, consulted three ways
 *
 * `siteAt(slot)` is the only thing that knows whether a slot holds an array and where its dishes go. Placement walks
 * it over the slots near a tile, the reservation walks it over the slots near a query point, and both get the same
 * answer by construction. The city established this pattern; deriving a footprint twice is how trees ended up inside
 * towers.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createLocalRng, deriveSeed } from './rng.js';
import { TELESCOPE } from '../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/** Slot index a coordinate falls in. */
function slotAt(coordinate, spacing) {
  return Math.round(coordinate / spacing - 0.5);
}

/**
 * How far the outermost dish sits from the site centre, plus the dish itself.
 *
 * Derived from the layout rather than written down, so changing the arm growth cannot leave the reach, the
 * reservation and the tile search disagreeing about how big an array is.
 */
function computeReach() {
  let distance = TELESCOPE.ARM_INNER;
  let furthest = distance;

  for (let i = 1; i < TELESCOPE.DISHES_PER_ARM; i += 1) {
    distance *= TELESCOPE.ARM_GROWTH;
    furthest = Math.max(furthest, distance);
  }

  return furthest + TELESCOPE.DISH_RADIUS;
}

const SITE_REACH = computeReach();

/**
 * How far a site may be jittered from its slot centre.
 *
 * Clamped against the grid regardless of the configured value, so raising `SITE_JITTER` past what the spacing allows
 * cannot let two arrays overlap. That guarantee is arithmetic rather than a collision check, like the city's streets.
 */
const SITE_JITTER = Math.max(
  0,
  Math.min(
    TELESCOPE.SITE_JITTER,
    (TELESCOPE.SITE_SPACING - 2 * (SITE_REACH + TELESCOPE.CLEARANCE)) / 2,
  ),
);

/** Furthest any part of a site can be from its slot centre. Sets how wide the reservation has to look. */
const SLOT_INFLUENCE = SITE_JITTER + SITE_REACH + TELESCOPE.CLEARANCE;

/**
 * Dish positions for a site, relative to its centre.
 *
 * A Y of three arms, with each dish further out than the last by a fixed factor — real arrays space theirs
 * logarithmically, and it looks purposeful rather than evenly stamped. One dish sits at the hub, which is what makes
 * the three arms read as arms rather than as three separate lines.
 *
 * @param {number} rotation - Rotation of the whole Y, radians.
 * @returns {Array<[number, number]>} Offsets in world units.
 */
function dishOffsets(rotation) {
  const offsets = [[0, 0]];

  for (let arm = 0; arm < TELESCOPE.ARMS; arm += 1) {
    const angle = rotation + (arm / TELESCOPE.ARMS) * Math.PI * 2;
    let distance = TELESCOPE.ARM_INNER;

    for (let i = 0; i < TELESCOPE.DISHES_PER_ARM; i += 1) {
      offsets.push([Math.cos(angle) * distance, Math.sin(angle) * distance]);
      distance *= TELESCOPE.ARM_GROWTH;
    }
  }

  return offsets;
}

/**
 * Build the shared site rule.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Derives an independent stream from everything else in the world.
 * @returns {(slotX: number, slotZ: number, sampleWeight: Function) => object|null}
 */
function createSites(seed, biomeId) {
  const siteSeed = deriveSeed(seed, `telescopes:${biomeId}`);

  return function siteAt(slotX, slotZ, sampleWeight) {
    const centerX = (slotX + 0.5) * TELESCOPE.SITE_SPACING;
    const centerZ = (slotZ + 0.5) * TELESCOPE.SITE_SPACING;

    const weight = sampleWeight(centerX, centerZ);
    if (weight <= 0) return null;

    /*
      One stream per slot, keyed by slot index rather than by tile, so an array does not depend on which tile is being
      built or the order tiles are visited in.

      Roll order is part of the world's identity. Reordering these, or inserting one before an existing one, moves and
      re-aims every array at every seed. Append only.
    */
    const random = createLocalRng(siteSeed, slotX, slotZ);

    const presenceRoll = random();
    const rotationRoll = random();
    const azimuthRoll = random();
    const elevationRoll = random();
    const jitterRollX = random();
    const jitterRollZ = random();

    /*
      Density scales with biome weight, so an array is less likely near a region's edge than in the middle of it.
      Unlike a scatter this does not thin an array out — a site either happens or it does not.
    */
    if (presenceRoll >= TELESCOPE.SITE_DENSITY * weight) return null;

    const x = centerX + (jitterRollX * 2 - 1) * SITE_JITTER;
    const z = centerZ + (jitterRollZ * 2 - 1) * SITE_JITTER;

    return {
      x,
      z,
      reach: SITE_REACH,

      /** Rotation of the whole Y, so two arrays are not stamped at the same angle. */
      rotation: rotationRoll * Math.PI * 2,

      /*
        Aimed once for the whole site. **This is the signature of a real array** — a field of identical hardware all
        pointing at one patch of sky reads as deliberate, where dishes at random angles read as scattered junk.
      */
      azimuth: azimuthRoll * Math.PI * 2,
      elevation: lerp(TELESCOPE.MIN_ELEVATION_ANGLE, TELESCOPE.MAX_ELEVATION_ANGLE, elevationRoll),

      weight,
      offsets: dishOffsets(rotationRoll * Math.PI * 2),
    };
  };
}

/**
 * Whether the ground under a whole site will do, and where each dish would stand.
 *
 * **All or nothing.** Half an array is a broken array, not a rarer one, so every dish position is checked before any
 * is emitted. Two separate limits, because they catch different things: the centre's slope stands in for "on a flat
 * spot", while the spread across the dishes catches a site that is gently sloped everywhere and still ends up with
 * one arm a long way below another.
 *
 * @param {object} site
 * @param {(x: number, z: number) => {elevation: number, slope: number}} sampleGroundDetail
 * @returns {Array<{x: number, z: number, groundY: number}>|null} Null when the site is unusable.
 */
function fitToGround(site, sampleGroundDetail) {
  if (sampleGroundDetail(site.x, site.z).slope > TELESCOPE.MAX_GROUND_SLOPE) return null;

  const positions = site.offsets.map(([dx, dz]) => {
    const x = site.x + dx;
    const z = site.z + dz;
    return { x, z, ...sampleGroundDetail(x, z) };
  });

  let lowest = Infinity;
  let highest = -Infinity;
  for (const { elevation } of positions) {
    lowest = Math.min(lowest, elevation);
    highest = Math.max(highest, elevation);
  }

  if (highest - lowest > TELESCOPE.MAX_SITE_SPREAD) return null;

  /*
    Each dish stands at its *own* ground height rather than on a graded pad. The ground is deliberately not
    flattened: terrain would then depend on structure placement, inverting the one-way rule that structures sample
    terrain and never the reverse. The cost is that an array steps slightly across a rise, which the spread limit
    above keeps within bounds.
  */
  return positions.map(({ x, z, elevation }) => ({
    x,
    z,
    groundY: elevation - TELESCOPE.FOOT_SINK,
  }));
}

/**
 * Range of slots whose sites could reach into a region.
 *
 * Note this is *not* the "slots whose centre falls inside" rule the scatters use. A site is hundreds of units across,
 * so a site centred outside a tile still puts dishes inside it. Ownership is settled per dish instead.
 */
function slotsNear(region) {
  const spacing = TELESCOPE.SITE_SPACING;

  return {
    firstX: slotAt(region.originX - SLOT_INFLUENCE, spacing),
    lastX: slotAt(region.originX + region.size + SLOT_INFLUENCE, spacing),
    firstZ: slotAt(region.originZ - SLOT_INFLUENCE, spacing),
    lastZ: slotAt(region.originZ + region.size + SLOT_INFLUENCE, spacing),
  };
}

/**
 * Build the array placement function for a biome.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createTelescopeArray(seed, biomeId) {
  const siteAt = createSites(seed, biomeId);

  /**
   * @param {{originX: number, originZ: number, size: number}} region - Tile bounds.
   * @param {object} context
   * @param {(x: number, z: number) => number} context.sampleWeight - This biome's weight.
   * @param {(x: number, z: number) => {elevation: number, slope: number}} context.sampleGroundDetail
   * @param {(x: number, z: number, radius: number) => boolean} [context.isReserved]
   * @returns {Array<object>} Dish descriptions, only those standing inside the region.
   */
  return function collectStructures(region, { sampleWeight, sampleGroundDetail, isReserved }) {
    const slots = slotsNear(region);
    const dishes = [];

    for (let slotX = slots.firstX; slotX <= slots.lastX; slotX += 1) {
      for (let slotZ = slots.firstZ; slotZ <= slots.lastZ; slotZ += 1) {
        const site = siteAt(slotX, slotZ, sampleWeight);
        if (!site) continue;

        const positions = fitToGround(site, sampleGroundDetail);
        if (!positions) continue;

        // Keep the whole installation clear of another biome's structures, tested at the hub and at every dish.
        if (positions.some(({ x, z }) => isReserved?.(x, z, TELESCOPE.DISH_RADIUS + TELESCOPE.CLEARANCE))) {
          continue;
        }

        for (const position of positions) {
          /*
            One tile owns each dish, decided by which tile the dish stands in. The site is shared between tiles, so
            this is the only place ownership can be settled — and getting it wrong emits a dish twice, at double
            brightness, which is the mistake farmland's fields made.
          */
          if (position.x < region.originX || position.x >= region.originX + region.size) continue;
          if (position.z < region.originZ || position.z >= region.originZ + region.size) continue;

          dishes.push({
            kind: 'dish',
            x: position.x,
            z: position.z,
            groundY: position.groundY,

            /*
              Which array this dish belongs to. Carried because a dish is only meaningful as part of one, and because
              a site is split across tiles — without it there is no way to ask whether an array came out whole.
            */
            siteX: site.x,
            siteZ: site.z,

            radius: TELESCOPE.DISH_RADIUS,
            mountHeight: TELESCOPE.MOUNT_HEIGHT,
            rings: TELESCOPE.DISH_RINGS,
            sides: TELESCOPE.DISH_SIDES,

            // Shared across the site, which is what makes the array read as one instrument.
            azimuth: site.azimuth,
            elevation: site.elevation,
            spin: 0,

            weight: site.weight,
          });
        }
      }
    }

    return dishes;
  };
}

/**
 * Build the test that says whether a point is inside a telescope site.
 *
 * Published as a biome reservation so trees and rock formations keep out of an array. Like the arch clearance it
 * ignores the ground checks, so it reserves space around a handful of sites that were never built — which costs a
 * tree here and there and buys a test that needs no terrain sampling and answers at any coordinate.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @returns {(x: number, z: number, radius: number, context: object) => boolean}
 */
export function createTelescopeClearance(seed, biomeId) {
  const siteAt = createSites(seed, biomeId);

  return function isInsideASite(x, z, radius, { sampleWeight, cache }) {
    const spacing = TELESCOPE.SITE_SPACING;

    /*
      Only the slots whose site could actually reach this point, exactly as the city's reservation bounds itself by a
      block's reach rather than scanning a fixed ring.

      **This is the difference between the feature being free and costing five milliseconds a forest tile.** Every
      tree, rock column and farm structure in the world asks this question once, so the arithmetic here runs hundreds
      of times per tile. With a site confined to `SITE_JITTER` of its slot centre the range below is usually one slot;
      at the full slack the grid allows it was nine.
    */
    const margin = SLOT_INFLUENCE + radius;
    const firstX = slotAt(x - margin, spacing);
    const lastX = slotAt(x + margin, spacing);
    const firstZ = slotAt(z - margin, spacing);
    const lastZ = slotAt(z + margin, spacing);

    for (let slotX = firstX; slotX <= lastX; slotX += 1) {
      for (let slotZ = firstZ; slotZ <= lastZ; slotZ += 1) {
        const key = `telescope:${slotX},${slotZ}`;

        /*
          Memoised per tile, exactly as the city's reservation is, and **not optional**.

          Every tree, rock column and farm structure in the world asks this question once. Deriving the nine
          surrounding slots on each ask means nine biome-field samples per candidate, and a forest tile has hundreds
          of candidates — measured, that took a forest tile from 7.9 ms to 31.7 ms. With the memo a whole tile costs
          at most nine derivations.
        */
        let site = cache?.get(key);

        if (site === undefined) {
          site = siteAt(slotX, slotZ, sampleWeight);
          cache?.set(key, site);
        }

        if (!site) continue;

        /*
          A circle over the whole site rather than one per dish. Generous, and deliberately so: a clearing around an
          installation is what a real site has, and the alternative is ten distance tests per query on the hottest
          path in the project.
        */
        if (Math.hypot(x - site.x, z - site.z) < site.reach + TELESCOPE.CLEARANCE + radius) return true;
      }
    }

    return false;
  };
}

/** Dishes a complete site holds. Exported so tests and budgets can reason about one array. */
export const DISHES_PER_SITE = 1 + TELESCOPE.ARMS * TELESCOPE.DISHES_PER_ARM;

/** How far an array reaches from its centre, in world units. */
export { SITE_REACH };
